import { parseStringToApiaNumber } from './parseStringToApiaNumber';

const delimiters = [',', ';', '\t', '|'];
export class ApiaUtilParsers {
  /**
   * Toma un número sin separadores de miles y lo devuelve en el formato
   * establecido por los parámetros de Apia para separadores de miles y
   * separadores de decimales.
   */
  addThousSeparator(nStr: string | number): string {
    // Apia Original Function
    let str = String(nStr);
    str += '';
    const x =
      window.CHAR_DECIMAL_SEPARATOR && str.split(window.CHAR_DECIMAL_SEPARATOR);
    let x1 = x?.[0];
    const x2 =
      x && x.length > 1 && window.CHAR_DECIMAL_SEPARATOR
        ? `${window.CHAR_DECIMAL_SEPARATOR}${x[1]}`
        : '';
    const rgx = /(\d+)(\d{3})/;
    while (x1 && rgx.test(x1)) {
      x1 =
        x1 &&
        window.CHAR_THOUS_SEPARATOR &&
        x1.replace(rgx, `$1${window.CHAR_THOUS_SEPARATOR}$2`);
    }
    return x1 ? `${x1}${x2}` : String(nStr);
  }

  #parseString(value: string): string {
    return parseStringToApiaNumber(value) || '';
  }

  /**
   * Toma un número y lo devuelve formateado con los parámetros de Apia
   */
  apiaNumber(pValue: number): number | string;
  /**
   * Toma una cadena de caracteres y lo parsea en el formato de Apia.
   * Esta cadena de caracteres podría venir por ejemplo de un evento
   * onChange en un campo de tipo input.
   */
  apiaNumber(pValue: string): string;
  apiaNumber(pValue: string | number): number | string {
    if (typeof pValue === 'string') {
      return this.#parseString(pValue);
    }
    // Apia Classic Original Function
    let value = '';
    try {
      value = pValue.toFixed(parseInt(window.AMOUNT_DECIMAL_SEPARATOR, 10));
    } catch (e) {
      console.error(e);
    }
    let s = value;
    if (!s.includes('.')) {
      if (window.ADD_THOUSAND_SEPARATOR) {
        s = this.addThousSeparator(s);
      }
      return s;
    }
    if (window.ADD_THOUSAND_SEPARATOR) {
      s = this.addThousSeparator(s.replace('.', window.CHAR_DECIMAL_SEPARATOR));
    } else {
      s = s.replace('.', window.CHAR_DECIMAL_SEPARATOR);
    }
    const int = s.split(window.CHAR_DECIMAL_SEPARATOR)[0];
    let decimals: string = s.split(window.CHAR_DECIMAL_SEPARATOR)[1];
    let decCount: number = decimals.length - 1;
    while (
      decCount >= parseInt(window.AMOUNT_DECIMAL_ZEROS, 10) &&
      decimals.charAt(decCount) === '0'
    ) {
      decimals = decimals.substring(0, decCount);
      decCount -= 1;
    }
    return (
      int +
      (decimals || decimals === '0'
        ? window.CHAR_DECIMAL_SEPARATOR + decimals
        : '')
    );
  }

  /**
   * Toma un número en formato Apia y lo devuelve como float en javascript.
   */
  jsNumber(value: string): number {
    // Apia Classic Original Function
    let re;
    if (window.CHAR_THOUS_SEPARATOR === '.')
      re = new RegExp(`\\${window.CHAR_THOUS_SEPARATOR}`, 'g');
    else
      re =
        window.CHAR_THOUS_SEPARATOR &&
        new RegExp(window.CHAR_THOUS_SEPARATOR, 'g');
    let aux = re && value.replace(re, '');
    aux =
      aux &&
      window.CHAR_DECIMAL_SEPARATOR &&
      aux.replace(window.CHAR_DECIMAL_SEPARATOR, '.');

    if (typeof aux !== 'string') {
      return parseFloat(value);
    }
    return aux ? parseFloat(aux) : parseFloat(value);
  }

  /**
   * Hay muchos valores que podrían querer ser interpretados como booleano
   *
   * Devuelve true si el valor coincide con true|T|t|1 y false si coincide con
   * false|F|f|0. Si no coincide con ninguna de éstas hace throw de un error.
   */
  parseBool(val: string): boolean {
    if (/^(?:true|T|t|1)$/i.test(val)) return true;
    if (/^(?:false|F|f|0)$/i.test(val)) return false;
    throw new Error('Property/Value is a boolean, try using true or false');
  }

  /**
   * Toma una estructura bidimensional y la convierte a csv, usando el método
   * String(...) sobre cada celda de datos.
   */
  toCsv(data: any[][], delimiter = ',') {
    return data.map((c) => c.map((d) => String(d)).join(delimiter)).join('\n');
  }

  /**
   * Inspecciona las primeras filas de un array de strings, en búsqueda del mejor delimitador
   * para convertir esos strings a array de datos, que serán el resultado de haber interpretado
   * un documento CSV.
   */
  #findDelimiter(rows: string[]) {
    const maxColsByDelimiter: Record<string, number> = {};

    for (const delimiter of delimiters) {
      const headCols = rows[0].split(delimiter).length;

      let allTrue = true;
      for (let i = 1; i < Math.min(30, rows.length) && allTrue; i++) {
        allTrue = allTrue && headCols === rows[i].split(delimiter).length;
      }

      if (allTrue) {
        maxColsByDelimiter[delimiter] = headCols;
      }
    }

    let max = 0;
    let bestDelimiter: string | null = null;

    Object.entries(maxColsByDelimiter).forEach(([delimiter, count]) => {
      if (count > max) {
        max = count;
        bestDelimiter = delimiter;
      }
    });

    if (bestDelimiter !== null) {
      return bestDelimiter as string;
    }

    throw new Error(
      `Cannot determine delimiter, it's none of: ${delimiters.join(', ')}`,
    );
  }

  #parseCellValue(cellValue: string) {
    return cellValue.match(/^\s*"?([^"]+)"?[\s\r\n]*$/)?.[1] ?? cellValue;
  }

  /**
   * Convierte un string a array de arrays, parseándolo en formato CSV, encontrando el delimitador en forma automática.
   */
  fromCsv(data: string): string[][] {
    const rows = data.split(/\r?\n\r?/).filter((row) => !row.match(/^\s*$/));
    const delimiter = this.#findDelimiter(rows);

    return rows.map((c) => {
      const a = c.split(delimiter);
      if (a[0][a[0].length - 1] === '') {
        a.pop();
      }
      return a.map((d) => this.#parseCellValue(d));
    });
  }
}
